/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from 'react';
import AbilityBlock from './AbilityBlock';
import HiddenStateSelector from './HiddenStateSelector';
import HiddenActionText from './HiddenActionText';
import EditableText from './EditableText';
import Abilities from './model/Abilities';
import Monster from './model/Monster';
import NpcData from './model/NpcData';
import Section from './model/Section';


interface AbilitiesBlockProps {
  name: string;
  useMm2024: boolean;
  mon: Monster;
  sectionName: string;
  sectionOptions: string[];
  values: Ability[];
  preText?: string;
  postText?: string;
  side: 'left' | 'right' | 'all';
  start: number;
  alwaysShow: boolean;
  blockType: string;
  openIndex: number;
  setOpenIndex: (index: number) => void;
  onSave: (abilities: Ability[]) => void;
  onAddLeftItem: () => number;
  onNpcUpdate: (npcData: NpcData) => void;
  onPreTextSave?: (text: string | number) => void;
  onPostTextSave?: (text: string | number) => void;
  onSectionChange: (newSectionName: string, section: Section | Ability, oldSectionName: string) => void;
  searchOpen: boolean;
  setSearchOpen: (searchOpen: boolean) => void;
  spellbookOpen: boolean;
  setSpellbookOpen: (spellbookOpen: boolean) => void;
  onRefreshTour: (tourState: string) => void;
}

const AbilitiesBlock: React.FC<AbilitiesBlockProps> = ({
  name,
  mon,
  useMm2024,
  sectionName,
  sectionOptions,
  values,
  preText,
  postText,
  side,
  start,
  alwaysShow,
  blockType,
  openIndex,
  setOpenIndex,
  onSave,
  onAddLeftItem,
  onNpcUpdate,
  onPreTextSave,
  onPostTextSave,
  onSectionChange,
  searchOpen, setSearchOpen,
  spellbookOpen, setSpellbookOpen,
  onRefreshTour
}) => {
  const [state, setState] = useState<string>('');
  const [states, setStates] = useState<string[]>([]);
  const [singleName, setSingleName] = useState<string>('');

  let insertIndex: number;
  // start = the full index of the start of the current block
  if (side === 'left') {
    insertIndex = Math.min(values.length, Math.max(0, mon.splitPoint - start));
  } else {
    insertIndex = values.length;
  }

  useEffect(() => {
    if (name === 'Spell List') {
      setStates(['Compressed', 'Expanded']);
      setState('Compressed');
    }
    setSingleName(getSingleName(name));
  }, [name]);

  const getSingleName = (plural: string): string => {
    if (plural.endsWith('s')) {
      return plural.substring(0, plural.length - 1);
    }
    switch (plural) {
      case '': return 'Ability';
      case 'Inventory': return 'Item';
      case 'Spells List': return 'Spell';
      case 'Mythic': return 'Mythic';
      default: return plural;
    }
  };

  if (!values?.length && !alwaysShow) {
    return null;
  }

  if ((side === 'left') && (start >= mon.splitPoint)) {
    return null;
  }

  if (side === 'right') {
    let lastItemIndex = start + values.length;
    if (preText || (values.length > 0 && alwaysShow)) lastItemIndex++;
    if (postText || (values.length > 0 && alwaysShow)) lastItemIndex++;
    if (lastItemIndex < mon.splitPoint) {
      return null;
    }

    if (start < mon.splitPoint) {
      name = '';
    }
  }

  const handleMoveUp = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    if (index > 0) {
      const newValues = [...values];
      [newValues[index - 1], newValues[index]] = [newValues[index], newValues[index - 1]];
      onSave(newValues);
      setOpenIndex(index - 1);
    }
  };

  const handleMoveDown = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    if (index < values.length - 1) {
      const newValues = [...values];
      [newValues[index + 1], newValues[index]] = [newValues[index], newValues[index + 1]];
      onSave(newValues);
      setOpenIndex(index + 1);
    }
  };

  const shouldShow = (index: number, splitPoint: number, side: string): boolean => {
    if (side === 'left') return index < splitPoint;
    if (side === 'right') return index >= splitPoint;
    return true;
  };

  let preTextContainer = null;
  if ((alwaysShow && values.length > 0) || preText) {
    if (onPreTextSave && shouldShow(start, mon.splitPoint, side)) {
      preTextContainer = (alwaysShow || preText) && (
        <section style={{ marginTop: '5px' }}>
          <EditableText
            initialValue={preText || ''}
            width="100%"
            type="textarea"
            alwaysShow={(alwaysShow && values.length > 0)}
            onSave={onPreTextSave}
            displayProcessor={(desc) => Abilities.ReplaceTags(desc, mon)}
            useMarkdown={true}
          />
        </section>
      );
    }
    start++;
  }

  let postTextContainer = null;
  if ((alwaysShow && values.length > 0) || postText) {
    if (onPostTextSave && shouldShow((start + values.length), mon.splitPoint, side)) {
      postTextContainer = (alwaysShow || postText) && (
        <section style={{ marginTop: '5px' }}>
          <EditableText
            initialValue={postText || ''}
            width="100%"
            type="textarea"
            alwaysShow={(alwaysShow && values.length > 0)}
            onSave={onPostTextSave}
            displayProcessor={(desc) => Abilities.ReplaceTags(desc, mon)}
            useMarkdown={true}
          />
        </section>
      );
    }
  }

  const content = values.map((o: Ability, index: number) => (
    shouldShow((start + index), mon.splitPoint, side) && (
      <AbilityBlock
        blockType={blockType}
        key={o.id || index}
        obj={o}
        mon={mon}
        sectionName={sectionName}
        section={o}
        sectionOptions={sectionOptions}
        onSectionChange={onSectionChange}
        npcData={mon.npcData || null}
        isOpen={openIndex === index}
        setIsOpen={(val) => setOpenIndex(val ? index : -1)}
        onNpcUpdate={onNpcUpdate}
        onSave={(newObj) => {
          const newValues = [...values];
          newValues[index] = { ...newValues[index], ...newObj };
          onSave(newValues);
        } }
        onDelete={() => {
          const newValues = values.filter((_, i) => i !== index);
          onSave(newValues);
        } }
        onMoveUp={(event) => handleMoveUp(event, index)}
        onMoveDown={(event) => handleMoveDown(event, index)}
        isFirst={index === 0}
        isLast={index === values.length - 1}
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        spellbookOpen={spellbookOpen}
        setSpellbookOpen={setSpellbookOpen}
        onRefreshTour={onRefreshTour}
        />
    )
  ));

  const defaultPreText: Record<string, string> = {
    'Legendary Actions': "The [MON] can take 3 legendary actions, choosing from the options below. Only one legendary action option can be used at a time and only at the end of another creature's turn. The [MON] regains spent legendary actions at the start of its turn.",
    'Mythic': "If the [MON]'s mythic trait is active, it can use the options below as legendary actions for 1 hour after using {Some Ability}.",
    'Lair Actions': "When fighting inside its lair, the [MON] can invoke the ambient magic to take lair actions. On initiative count 20 (losing initiative ties), the [MON] can take one lair action to cause one of the following effects:",
    'Regional Effects': "The region containing the [MON]'s lair is warped by the creature's presence, which creates one or more of the following effects:",
  };

  const defaultPostText: Record<string, string> = {
    'Lair Actions': "The [MON] can't repeat an effect until they have all been used, and it can't use the same effect two rounds in a row.",
    'Regional Effects': "If the [MON] dies, the effects fade over the course of 3d10 days.",
  };


  const handleAddItem = () => {
    if (values.length === 0) {
      if (onPreTextSave && defaultPreText[name]) {
        onPreTextSave(defaultPreText[name]);
      }
      if (onPostTextSave && defaultPostText[name]) {
        onPostTextSave(defaultPostText[name]);
      }
    }
    const newValue: Ability = {
      name: 'new ' + singleName,
      desc: singleName + ' description',
      id: (values.reduce((acc: number, curr: Ability) => ((curr.id && (curr.id > acc)) ? curr.id : acc), 0) + 1),
    };
    if (side === 'left') {
      onAddLeftItem && onAddLeftItem();
    }
    const newValues = [...values];
    newValues.splice(insertIndex, 0, newValue);
    onSave(newValues);
    setTimeout(() => setOpenIndex(insertIndex), 10);
  };

  return (
    <div className="property-block">
      {(name && !(name === 'Traits' && !useMm2024)) ? (
        <h3 className='block-header-font'>{name}
          {state ? (
            <HiddenStateSelector
              initialState={state}
              states={states}
              onSave={(newVal) => { setState(newVal); }}
            />
          ) : null}
        </h3>
      ) : ''}
      {preTextContainer}
      {content}
      {postTextContainer}
      <section>
        <HiddenActionText value={'Add ' + singleName} onClick={handleAddItem} alwaysShow={alwaysShow}/>
      </section>
    </div>
  );
};

export default AbilitiesBlock;
